import type { Dimension, MatrixFor } from "@src/math/types";
import {
  compose,
  copyMatrix,
  identity,
  matrix as toMatrix,
  multiply,
} from "@src/math/matrix";

export type AnimationMode = "steps" | "composed";

export interface AnimationFrame {
  readonly mode: AnimationMode;
  readonly elapsedMs: number;
}

export interface AnimationMatrix<D extends Dimension> {
  readonly id: string;
  readonly values: Readonly<MatrixFor<D>>;
  readonly durationMs: number;
}

export interface AnimationSequence<D extends Dimension> {
  readonly dimension: D;
  readonly matrices: readonly AnimationMatrix<D>[];
}

export type AnimationProgress =
  | { mode: "steps"; matrixId: string; progress: number }
  | { mode: "composed"; progress: number };

interface AnimationPlan<D extends Dimension> {
  readonly sequence: AnimationSequence<D>;
  readonly steps: readonly PlannedStep<D>[];
  readonly totalStepDuration: number;
  readonly composedDuration: number;
}

interface PlannedStep<D extends Dimension> {
  readonly matrix: AnimationSequence<D>["matrices"][number];
  readonly duration: number;
  readonly cumulativeDuration: number;
  readonly suffixTransform: MatrixFor<D>;
}

interface ActiveStep<D extends Dimension> {
  readonly index: number;
  readonly progress: number;
  readonly step: PlannedStep<D>;
}

const animationPlans = new WeakMap<object, AnimationPlan<Dimension>>();

function getAnimationPlan<D extends Dimension>(
  sequence: AnimationSequence<D>,
): AnimationPlan<D> {
  const cached = animationPlans.get(sequence) as AnimationPlan<D> | undefined;
  if (cached) return cached;

  const steps = new Array<PlannedStep<D>>(sequence.matrices.length);
  let elapsed = 0;
  let suffix = identity(sequence.dimension);
  let composedDuration = 0;
  for (let index = sequence.matrices.length - 1; index >= 0; index -= 1) {
    const matrix = requireAt(sequence.matrices, index, "animation matrix");
    const duration = getMatrixDuration(matrix);
    elapsed += duration;
    steps[index] = {
      matrix,
      duration,
      cumulativeDuration: elapsed,
      suffixTransform: suffix,
    };
    composedDuration = Math.max(composedDuration, duration);
    suffix = multiply(sequence.dimension, matrix.values, suffix);
  }
  const plan: AnimationPlan<D> = {
    sequence,
    steps,
    totalStepDuration: elapsed,
    composedDuration,
  };
  animationPlans.set(sequence, plan);
  return plan;
}

function interpolateClampedMatrix<D extends Dimension>(
  dimension: D,
  from: Readonly<MatrixFor<D>>,
  to: Readonly<MatrixFor<D>>,
  amount: number,
): MatrixFor<D> {
  const progress = Math.min(1, Math.max(0, amount));
  const length = dimension === 2 ? 4 : 9;
  return toMatrix(
    dimension,
    Array.from({ length }, (_, index) => {
      const fromValue = requireAt(from, index, "source matrix value");
      const toValue = requireAt(to, index, "target matrix value");
      return fromValue + (toValue - fromValue) * progress;
    }),
  );
}

export function calculateAnimatedTransform<D extends Dimension>(
  sequence: AnimationSequence<D>,
  appliedTransform: Readonly<MatrixFor<D>>,
  frame: AnimationFrame | null,
  totalTransform: Readonly<MatrixFor<D>> = compose(
    sequence.dimension,
    sequence.matrices.map((matrix) => matrix.values),
  ),
): MatrixFor<D> {
  if (!frame) return copyMatrix(sequence.dimension, appliedTransform);
  switch (frame.mode) {
    case "composed":
      return getComposedTransform(sequence, frame.elapsedMs, totalTransform);
    case "steps":
      return getStepTransform(sequence, frame.elapsedMs);
  }
}

function getComposedTransform<D extends Dimension>(
  sequence: AnimationSequence<D>,
  elapsedMs: number,
  totalTransform: Readonly<MatrixFor<D>>,
): MatrixFor<D> {
  return interpolateClampedMatrix(
    sequence.dimension,
    identity(sequence.dimension),
    totalTransform,
    composedProgress(sequence, elapsedMs),
  );
}

export function getStepTransform<D extends Dimension>(
  sequence: AnimationSequence<D>,
  elapsedMs: number,
): MatrixFor<D> {
  const plan = getAnimationPlan(sequence);
  const activeStep = findActiveStep(plan, elapsedMs);
  if (!activeStep) {
    const first: PlannedStep<D> | undefined = plan.steps.at(0);
    return first === undefined
      ? identity(sequence.dimension)
      : multiply(
          sequence.dimension,
          first.matrix.values,
          first.suffixTransform,
        );
  }
  const partial = interpolateClampedMatrix(
    sequence.dimension,
    identity(sequence.dimension),
    activeStep.step.matrix.values,
    activeStep.progress,
  );
  return multiply(sequence.dimension, partial, activeStep.step.suffixTransform);
}

export function getAnimationProgress<D extends Dimension>(
  sequence: AnimationSequence<D>,
  frame: AnimationFrame | null,
): AnimationProgress | null {
  if (!frame) return null;

  switch (frame.mode) {
    case "composed":
      return {
        mode: "composed",
        progress: composedProgress(sequence, frame.elapsedMs),
      };
    case "steps":
      return getStepProgress(sequence, frame.elapsedMs);
  }
}

function getStepProgress<D extends Dimension>(
  sequence: AnimationSequence<D>,
  elapsedMs: number,
): Extract<AnimationProgress, { mode: "steps" }> | null {
  const activeStep = findActiveStep(
    getAnimationPlan(sequence),
    normalizeElapsedMs(elapsedMs),
  );
  return activeStep
    ? {
        mode: "steps",
        matrixId: activeStep.step.matrix.id,
        progress: activeStep.progress,
      }
    : null;
}

function composedProgress<D extends Dimension>(
  sequence: AnimationSequence<D>,
  elapsedMs: number,
): number {
  return Math.min(
    1,
    normalizeElapsedMs(elapsedMs) /
      Math.max(1, getAnimationPlan(sequence).composedDuration),
  );
}

function normalizeElapsedMs(elapsedMs: number): number {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0)
    throw new Error("Invalid animation elapsed time");
  return elapsedMs;
}

function findActiveStep<D extends Dimension>(
  plan: AnimationPlan<D>,
  elapsedMs: number,
): ActiveStep<D> | null {
  const elapsed = normalizeElapsedMs(elapsedMs);
  if (elapsed > plan.totalStepDuration || plan.steps.length === 0) return null;
  let low = 0;
  let high = plan.steps.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const step = requireAt(plan.steps, middle, "animation step");
    if (step.cumulativeDuration >= elapsed) low = middle + 1;
    else high = middle - 1;
  }
  const index = high;
  const step = requireAt(plan.steps, index, "animation step");
  const completedBefore = plan.steps[index + 1]?.cumulativeDuration ?? 0;
  return {
    index,
    progress: (elapsed - completedBefore) / step.duration,
    step,
  };
}

export function calculateAnimationDuration<D extends Dimension>(
  sequence: AnimationSequence<D>,
  mode: AnimationMode,
): number {
  const plan = getAnimationPlan(sequence);
  switch (mode) {
    case "composed":
      return plan.composedDuration;
    case "steps":
      return plan.totalStepDuration;
  }
}

export function getMatrixDuration(matrix: {
  readonly durationMs: number;
}): number {
  if (!Number.isFinite(matrix.durationMs) || matrix.durationMs <= 0)
    throw new Error("Invalid matrix duration");
  return matrix.durationMs;
}

function requireAt<T>(
  values: readonly T[],
  index: number,
  description: string,
): T {
  const value = values[index];
  if (value === undefined) throw new Error(`Missing ${description}`);
  return value;
}
